import { configureStore, combineReducers } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage"; // Local storage for web
import { persistReducer, persistStore } from "redux-persist";
import {
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist"; // Required for redux-persist
import agentReducer from "./features/agent/agentSlice";
import customerReducer from "./features/customer/customerSlice";
import cateringCustomerReducer from "./features/cateringCustomer/cateringCustomerSlice";
import companyReducer from "./features/company/companySlice";
import authReducer from "./features/auth/authSlice";
import countryReducer from "./features/country/countrySlice";
import restaurantReducer from "./features/restaurants/restaurantSlice";
import orderReducer from "./features/order/orderSlice";
import { resetAppState } from "./features/auth/authSlice";

// Create separate persist configs for each reducer
const customerPersistConfig = {
  key: "customer",
  storage,
  whitelist: ["selectedAddress", "searchedCustomer"], // Only persist necessary fields
};

const cateringCustomerPersistConfig = {
  key: "cateringCustomer",
  storage,
  whitelist: ["selectedAddress", "searchedCustomer"], // Only persist necessary fields
};

const companyPersistConfig = {
  key: "company",
  storage,
  whitelist: ["companyList", "searchedCompany", "companySelectedAddress"], // Only persist necessary fields
};

const restaurantPersistConfig = {
  key: "restaurants",
  storage,
};

// Remove order persist config since we don't want to persist orders
// const orderPersistConfig = {
//   key: "orders",
//   storage,
//   whitelist: ["orderInfo", "orderStatus", "orderType", "schedule", "pastOrderDetails"],
// };

// Wrap individual reducers with persistReducer
const persistedCustomerReducer = persistReducer(
  customerPersistConfig,
  customerReducer
);
const persistedCateringCustomerReducer = persistReducer(
  cateringCustomerPersistConfig,
  cateringCustomerReducer
);
const persistedCompanyReducer = persistReducer(
  companyPersistConfig,
  companyReducer
);
const persistedRestaurantReducer = persistReducer(
  restaurantPersistConfig,
  restaurantReducer
);
// Don't persist order reducer
// const persistedOrderReducer = persistReducer(
//   orderPersistConfig,
//   orderReducer
// );

// Configure persist options
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'agent', 'customer', 'cateringCustomer', 'company', 'restaurants'], // Orders are not included here
};

// Create appReducer (normal combined reducers)
const appReducer = combineReducers({
  agent: agentReducer,
  customer: persistedCustomerReducer,
  cateringCustomer: persistedCateringCustomerReducer,
  company: persistedCompanyReducer,
  auth: authReducer,
  country: countryReducer,
  restaurants: persistedRestaurantReducer,
  orders: orderReducer, // Use the non-persisted reducer directly
});

// Root reducer with state reset capability
const rootReducer = (state, action) => {
  // When the resetAppState action is dispatched, reset all state
  if (action.type === 'auth/resetAppState') {
    // For non-persisted reducers, clearing state is automatic
    // For persisted reducers, we need to clear localStorage explicitly
    if (typeof window !== 'undefined') {
      localStorage.removeItem('persist:root');
    }
    
    // Return undefined to each reducer, causing them to return their initial state
    return appReducer(undefined, action);
  }
  
  // Log rehydration issues (development only)
  if (action.type === REHYDRATE && state && state.restaurants && process.env.NODE_ENV === 'development') {
    if (state.restaurants.cateringCart) {
      const cateringCart = state.restaurants.cateringCart;
      if (!cateringCart || typeof cateringCart !== 'object' || 
          !Array.isArray(cateringCart.packages) || 
          !Array.isArray(cateringCart.items) ||
          !Array.isArray(cateringCart.cateringCartIds)) {
        
        console.warn('RootReducer: Detected corrupted cateringCart after rehydration');
      }
    }
  }
  
  return appReducer(state, action);
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these actions for redux-persist
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  // Add safety check for corrupted state
  preloadedState: undefined,
});

// Optional: Add store monitoring for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  store.subscribe(() => {
    const state = store.getState();
    
    // Check if cateringCart is corrupted and log warning
    if (state.restaurants && state.restaurants.cateringCart) {
      const cateringCart = state.restaurants.cateringCart;
      
      if (!cateringCart || typeof cateringCart !== 'object' || 
          !Array.isArray(cateringCart.packages) || 
          !Array.isArray(cateringCart.items) ||
          !Array.isArray(cateringCart.cateringCartIds)) {
        
        console.warn('Store: Detected corrupted cateringCart state');
      }
    }
  });
}

export const persistor = persistStore(store);

// Export the reset action for convenience
export const resetReduxState = () => store.dispatch(resetAppState());

export default store;
